import React, { useEffect, useMemo, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    Collapse,
    Table,
    TableBody,
    TableHead,
} from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getUserDetails } from "../../redux/userRelated/userHandle";
import {
    calculateOverallAttendancePercentage,
    calculateSubjectAttendancePercentage,
    groupAttendanceBySubject,
} from "../../components/attendanceCalculator";
import CustomBarChart from "../../components/CustomBarChart";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import {
    StudentPageContainer,
    StudentPageHeader,
    StudentPageLoading,
    StudentViewToggle,
    StudentEmptyState,
    ResultsSectionPaper,
    ResponsiveTableWrap,
    StudentStatCard,
} from "./studentPortalUi";
import { Grid } from "@mui/material";

const ViewStdAttendance = () => {
    const dispatch = useDispatch();
    const [openStates, setOpenStates] = useState({});
    const { userDetails, currentUser, loading } = useSelector((state) => state.user);
    const [subjectAttendance, setSubjectAttendance] = useState([]);
    const [view, setView] = useState("table");

    useEffect(() => {
        if (currentUser?._id) dispatch(getUserDetails(currentUser._id, "Student"));
    }, [dispatch, currentUser?._id]);

    useEffect(() => {
        if (userDetails) setSubjectAttendance(userDetails.attendance || []);
    }, [userDetails]);

    const attendanceBySubject = groupAttendanceBySubject(subjectAttendance);
    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const hasAttendance = Array.isArray(subjectAttendance) && subjectAttendance.length > 0;

    const subjectData = useMemo(
        () =>
            Object.entries(attendanceBySubject).map(([subName, { present, sessions }]) => ({
                subject: subName,
                attendancePercentage: calculateSubjectAttendancePercentage(present, sessions),
                totalClasses: sessions,
                attendedClasses: present,
            })),
        [attendanceBySubject],
    );

    const handleOpen = (subId) => {
        setOpenStates((prev) => ({ ...prev, [subId]: !prev[subId] }));
    };

    if (loading && !userDetails) {
        return <StudentPageLoading />;
    }

    return (
        <StudentPageContainer maxWidth="lg">
            <StudentPageHeader
                eyebrow="Attendance"
                title="My attendance"
                subtitle="Session-by-session records submitted by your teachers."
            />

            {!hasAttendance ? (
                <StudentEmptyState
                    icon={EventAvailableOutlinedIcon}
                    title="No attendance yet"
                    description="When teachers record attendance for your class, it will appear here with percentages per subject."
                />
            ) : (
                <>
                    <Grid container spacing={2.5} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <StudentStatCard
                                title="Overall"
                                value={Math.round(overallAttendancePercentage)}
                                hint="% present across subjects"
                                icon={EventAvailableOutlinedIcon}
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <StudentStatCard
                                title="Subjects"
                                value={Object.keys(attendanceBySubject).length}
                                hint="With attendance data"
                                icon={EventAvailableOutlinedIcon}
                                color="primary"
                            />
                        </Grid>
                    </Grid>

                    <StudentViewToggle
                        value={view}
                        onChange={setView}
                        options={[
                            { value: "table", label: "Table", icon: <TableChartOutlinedIcon fontSize="small" /> },
                            { value: "chart", label: "Chart", icon: <InsertChartOutlinedIcon fontSize="small" /> },
                        ]}
                    />

                    {view === "chart" ? (
                        <ResultsSectionPaper title="Attendance by subject">
                            <CustomBarChart chartData={subjectData} dataKey="attendancePercentage" />
                        </ResultsSectionPaper>
                    ) : (
                        <ResultsSectionPaper title="Subject breakdown">
                            <ResponsiveTableWrap>
                                <Table size="small">
                                    <TableHead>
                                        <StyledTableRow>
                                            <StyledTableCell>Subject</StyledTableCell>
                                            <StyledTableCell align="right">Present</StyledTableCell>
                                            <StyledTableCell align="right">Sessions</StyledTableCell>
                                            <StyledTableCell align="right">%</StyledTableCell>
                                            <StyledTableCell align="center">Details</StyledTableCell>
                                        </StyledTableRow>
                                    </TableHead>
                                    {Object.entries(attendanceBySubject).map(
                                        ([subName, { present, allData, subId, sessions }]) => {
                                            const pct = calculateSubjectAttendancePercentage(present, sessions);
                                            return (
                                                <TableBody key={subId || subName}>
                                                    <StyledTableRow>
                                                        <StyledTableCell>{subName}</StyledTableCell>
                                                        <StyledTableCell align="right">{present}</StyledTableCell>
                                                        <StyledTableCell align="right">{sessions}</StyledTableCell>
                                                        <StyledTableCell align="right">
                                                            <Chip size="small" label={`${pct}%`} color={pct >= 75 ? "success" : "default"} />
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => handleOpen(subId)}
                                                                endIcon={
                                                                    openStates[subId] ? (
                                                                        <KeyboardArrowUp />
                                                                    ) : (
                                                                        <KeyboardArrowDown />
                                                                    )
                                                                }
                                                            >
                                                                Sessions
                                                            </Button>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                    <StyledTableRow>
                                                        <StyledTableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                                                            <Collapse in={openStates[subId]} timeout="auto" unmountOnExit>
                                                                <Box sx={{ py: 1.5, px: 1 }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <StyledTableRow>
                                                                                <StyledTableCell>Date</StyledTableCell>
                                                                                <StyledTableCell align="right">Status</StyledTableCell>
                                                                            </StyledTableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {(allData || []).map((data, index) => {
                                                                                const date = new Date(data.date);
                                                                                const dateString =
                                                                                    date.toString() !== "Invalid Date"
                                                                                        ? date.toISOString().substring(0, 10)
                                                                                        : "—";
                                                                                return (
                                                                                    <StyledTableRow key={index}>
                                                                                        <StyledTableCell>{dateString}</StyledTableCell>
                                                                                        <StyledTableCell align="right">
                                                                                            {data.status}
                                                                                        </StyledTableCell>
                                                                                    </StyledTableRow>
                                                                                );
                                                                            })}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                </TableBody>
                                            );
                                        },
                                    )}
                                </Table>
                            </ResponsiveTableWrap>
                        </ResultsSectionPaper>
                    )}
                </>
            )}
        </StudentPageContainer>
    );
};

export default ViewStdAttendance;
